/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Space,
  Stack,
  Text,
  TextLabel,
  Tooltip,
  useColorMode,
  useColorStyle,
  useTheme,
} from '@tonic-one/react';
import {
  BackupIcon,
  ExternalLinkIcon,
  SearchOIcon,
} from '@tonic-one/react-icons';
import { useToggle } from '@tonic-one/react-hooks';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import noDataSVG from 'src/assets/image/noData.svg';

import { useAccountStore } from '../../../models/useAccountModel';
import { useUtcOffsetStore } from '../../../models/useUtcOffsetModel';
import { LogTypeEnum, QueryHistoryItem } from '../../../types/log';
import { getUtcTime } from '../../../utils/time';
import { useLogReducerContext } from '../context';
import { LogTimeUnit } from '../shared';
import { handleRedirectClick } from '@Utils/common';

function QueryHistoryModal({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { offset } = useUtcOffsetStore();
  const { groupCompanyGuid } = useAccountStore();
  const { setQueryParams } = useLogReducerContext();
  const [colorMode] = useColorMode();
  const borderColor = colorMode === 'dark' ? 'gray:70' : 'gray:20';
  const queryHistory: QueryHistoryItem[] = JSON.parse(
    localStorage.getItem(`casLogQueryHistory.${groupCompanyGuid}`) || '[]'
  );
  const dateFormat = (date: string | undefined) => {
    return getUtcTime(date, offset, t('common.momentDateFormat'));
  };

  const onSearch = useCallback((history: QueryHistoryItem) => {
    setQueryParams(
      history.type as LogTypeEnum,
      {
        predefined: LogTimeUnit.Custom,
        start: history.timeRange.start,
        end: history.timeRange.end,
      },
      [],
      history.criteria.map((item) => {
        return {
          ...item,
          label: t(`log.fields.${item.field}`),
        };
      })
    );
    onClose();
  }, []);

  const onOpenNewTab = useCallback((history: QueryHistoryItem) => {
    handleRedirectClick(
      `/logs?` +
        `type=${history.type}&timeRange=${JSON.stringify({
          predefined: LogTimeUnit.Custom,
          start: history.timeRange.start,
          end: history.timeRange.end,
        })}&criteria=${JSON.stringify(history.criteria)}`,
      '_blank'
    );
  }, []);

  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={visible}
      onClose={onClose}
      size="lg"
    >
      <ModalOverlay opacity={0.9} />
      <ModalContent>
        <ModalHeader>{t('log.modals.viewHistory.title')}</ModalHeader>
        <ModalBody>
          {queryHistory.length > 0 ? (
            <Stack height="55vh">
              {queryHistory.map((history, index) => {
                return (
                  <Flex key={index}>
                    <Box flex="auto">
                      <Stack my="1x">
                        <Stack
                          border={1}
                          borderColor={borderColor}
                          padding="2x"
                          gap="1x"
                        >
                          <Flex pl="20px">
                            <TextLabel>{t('log.labels.logType')}</TextLabel>
                            :&nbsp;
                            <Text>{t(`log.type.${history.type}`)}</Text>
                          </Flex>
                          <Flex pl="20px">
                            <TextLabel>
                              {t('log.placeholders.period')}
                            </TextLabel>
                            :&nbsp;
                            <Text>{`${dateFormat(
                              history.timeRange.start
                            )} - ${dateFormat(history.timeRange.end)}`}</Text>
                          </Flex>
                        </Stack>

                        <Flex
                          border={1}
                          borderTop={0}
                          borderColor={borderColor}
                          px="12px"
                          py="5px"
                          wrap="wrap"
                        >
                          {history.criteria.length > 0 ? (
                            history.criteria.map((item) => {
                              return (
                                <CriteriaItem
                                  key={item.field + item.value}
                                  field={item.field}
                                  value={item.value}
                                />
                              );
                            })
                          ) : (
                            <TextLabel>
                              {t('log.placeholders.noSearchCriteria')}
                            </TextLabel>
                          )}
                        </Flex>

                        <Flex justifyContent="flex-end" py="1x">
                          <TextLabel>{t('log.placeholders.applied')}</TextLabel>
                          :&nbsp;
                          <Text>{dateFormat(history.applied)}</Text>
                        </Flex>
                      </Stack>
                    </Box>
                    <Box flex="initial" pt="1x">
                      <Stack>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            onSearch(history);
                          }}
                        >
                          <Icon as={SearchOIcon} size="4x" />
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            onOpenNewTab(history);
                          }}
                        >
                          <Tooltip
                            placement="left"
                            label={t('log.hint.queryInNewTab')}
                          >
                            <Icon as={ExternalLinkIcon} size="4x" />
                          </Tooltip>
                        </Button>
                      </Stack>
                    </Box>
                  </Flex>
                );
              })}
            </Stack>
          ) : (
            <Stack
              justifyContent="center"
              alignItems="center"
              height="55vh"
              spacing="6x"
            >
              <Image src={noDataSVG} />
              <Text color="white:secondary">{t('reportCommon.noData')}</Text>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter justifyContent="flex-end">
          <Button onClick={onClose}>{t('common.button.close')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

const CriteriaItem = ({ field, value }: { field: string; value: string }) => {
  const { t } = useTranslation('log');
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { colors } = useTheme();
  return (
    <Box>
      <span
        style={{
          color: colors[colorStyle.color.secondary],
        }}
      >
        {t(`log.fields.${field}`)}:&nbsp;
      </span>
      {value + ';'}
      <Space width="1x" />
    </Box>
  );
};

export const QueryHistoryBtn = () => {
  const { t } = useTranslation('log');
  const [isOpen, toggleIsOpen] = useToggle(false);
  return (
    <>
      <Button variant="secondary" onClick={toggleIsOpen}>
        <Icon as={BackupIcon} />
        &nbsp;{t('log.button.viewHistory')}
      </Button>
      <QueryHistoryModal visible={isOpen} onClose={toggleIsOpen} />
    </>
  );
};
